import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import "./PlainTextComposer.css";
const BLOCK_TAGS = new Set([
    "ADDRESS", "ARTICLE", "ASIDE", "BLOCKQUOTE", "DIV", "DL", "FIELDSET",
    "FIGURE", "FOOTER", "FORM", "H1", "H2", "H3", "H4", "H5", "H6",
    "HEADER", "HR", "LI", "MAIN", "NAV", "OL", "P", "PRE", "SECTION",
    "TABLE", "TR", "UL",
]);
function isBlockElement(node) {
    return node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName);
}
/**
 * Convert the browser's contenteditable paragraph representation into text.
 * A lone <br> is the empty-editor marker, while <br> in a non-empty block is
 * a real line break. Block siblings receive one separator between them.
 */
function plainTextFromNode(node) {
    if (node.nodeType === Node.TEXT_NODE)
        return node.nodeValue ?? "";
    if (node.nodeType !== Node.ELEMENT_NODE)
        return "";
    const element = node;
    if (element.tagName === "BR")
        return "\n";
    const children = Array.from(element.childNodes);
    if (children.length === 1
        && children[0].nodeType === Node.ELEMENT_NODE
        && children[0].tagName === "BR") {
        return "";
    }
    return children.map((child, index) => {
        const text = plainTextFromNode(child);
        if (isBlockElement(child) && index < children.length - 1)
            return `${text}\n`;
        return text;
    }).join("");
}
function textLength(node) {
    return plainTextFromNode(node).length;
}
/**
 * Find a selection point in the normalized text stream. This is deliberately
 * independent of Range#toString(), whose treatment of contenteditable block
 * elements differs across browsers.
 */
function getPlainTextOffset(root, target, targetOffset) {
    let result = 0;
    let found = false;
    const visit = (node) => {
        if (found)
            return;
        if (node === target) {
            if (node.nodeType === Node.TEXT_NODE) {
                result += Math.min(targetOffset, node.nodeValue?.length ?? 0);
            }
            else {
                const children = Array.from(node.childNodes);
                const childCount = Math.min(targetOffset, children.length);
                result += children.slice(0, childCount).reduce((sum, child, index) => {
                    const separator = isBlockElement(child) && index < children.length - 1 ? 1 : 0;
                    return sum + textLength(child) + separator;
                }, 0);
            }
            found = true;
            return;
        }
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.nodeValue?.length ?? 0;
            return;
        }
        const children = Array.from(node.childNodes);
        children.forEach((child, index) => {
            if (found)
                return;
            visit(child);
            if (!found && isBlockElement(child) && index < children.length - 1)
                result += 1;
        });
    };
    visit(root);
    return found ? result : plainTextFromNode(root).length;
}
function restoreSelection(root, anchorOffset, focusOffset) {
    const plainText = root.textContent ?? "";
    root.textContent = "";
    const selection = window.getSelection();
    if (!selection)
        return;
    if (!plainText) {
        const emptyRange = document.createRange();
        emptyRange.selectNodeContents(root);
        emptyRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(emptyRange);
        return;
    }
    const textNode = document.createTextNode(plainText);
    root.appendChild(textNode);
    const anchor = Math.min(anchorOffset, textNode.length);
    const focus = Math.min(focusOffset, textNode.length);
    const range = document.createRange();
    range.setStart(textNode, Math.min(anchor, focus));
    range.setEnd(textNode, Math.max(anchor, focus));
    selection.removeAllRanges();
    selection.addRange(range);
}
/**
 * A controlled, plain-text chat composer.
 *
 * A contenteditable is used instead of an input/textarea so the composer can
 * grow naturally when a message contains line breaks. The value exposed to
 * consumers is always textContent; pasted markup is inserted as a text node
 * and can never become part of the message.
 */
const PlainTextComposer = ({ value, onChange, onSend, disabled = false, placeholder = "", ariaLabel, className, style, "data-testid": dataTestId, }) => {
    const composerRef = useRef(null);
    const composingRef = useRef(false);
    const compositionJustEndedRef = useRef(false);
    const compositionResetTimerRef = useRef(null);
    useEffect(() => () => {
        if (compositionResetTimerRef.current !== null) {
            window.clearTimeout(compositionResetTimerRef.current);
        }
    }, []);
    // Keep external draft changes (most importantly the clear after sending)
    // reflected in the editing surface without moving the caret on every input.
    useLayoutEffect(() => {
        const composer = composerRef.current;
        if (!composer || composingRef.current)
            return;
        if ((composer.textContent ?? "") !== value) {
            composer.textContent = value;
        }
    }, [value]);
    const readValue = useCallback(() => composerRef.current?.textContent ?? value, [value]);
    const normalizeComposer = useCallback(() => {
        const composer = composerRef.current;
        if (!composer)
            return "";
        const selection = typeof window !== "undefined" ? window.getSelection() : null;
        const selectionInside = !!selection
            && selection.rangeCount > 0
            && composer.contains(selection.anchorNode)
            && composer.contains(selection.focusNode);
        const anchorOffset = selectionInside
            ? getPlainTextOffset(composer, selection.anchorNode, selection.anchorOffset)
            : 0;
        const focusOffset = selectionInside
            ? getPlainTextOffset(composer, selection.focusNode, selection.focusOffset)
            : anchorOffset;
        const plainText = plainTextFromNode(composer);
        const hasMarkup = composer.querySelector("*") !== null;
        if (hasMarkup) {
            composer.textContent = plainText;
            if (selectionInside) {
                restoreSelection(composer, anchorOffset, focusOffset);
            }
        }
        return plainText;
    }, []);
    const emitValue = useCallback(() => {
        onChange(normalizeComposer());
    }, [normalizeComposer, onChange]);
    const insertPlainText = useCallback((text) => {
        const composer = composerRef.current;
        if (!composer || disabled)
            return;
        composer.focus();
        const selection = typeof window !== "undefined" ? window.getSelection() : null;
        let range;
        if (selection && selection.rangeCount > 0 && composer.contains(selection.anchorNode)) {
            range = selection.getRangeAt(0);
        }
        else {
            range = document.createRange();
            range.selectNodeContents(composer);
            range.collapse(false);
            selection?.removeAllRanges();
            selection?.addRange(range);
        }
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
        emitValue();
    }, [disabled, emitValue]);
    const handleInput = useCallback(() => {
        if (disabled)
            return;
        emitValue();
    }, [disabled, emitValue]);
    const handlePaste = useCallback((event) => {
        event.preventDefault();
        insertPlainText(event.clipboardData.getData("text/plain"));
    }, [insertPlainText]);
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        insertPlainText(event.dataTransfer.getData("text/plain"));
    }, [insertPlainText]);
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleCompositionStart = useCallback(() => {
        if (disabled)
            return;
        composingRef.current = true;
        compositionJustEndedRef.current = false;
        if (compositionResetTimerRef.current !== null) {
            window.clearTimeout(compositionResetTimerRef.current);
            compositionResetTimerRef.current = null;
        }
    }, []);
    const handleCompositionEnd = useCallback(() => {
        if (disabled)
            return;
        composingRef.current = false;
        // Some IMEs dispatch a second Enter keydown immediately after
        // compositionend. Consume that keydown so confirming a candidate cannot
        // accidentally send the draft.
        compositionJustEndedRef.current = true;
        compositionResetTimerRef.current = window.setTimeout(() => {
            compositionJustEndedRef.current = false;
            compositionResetTimerRef.current = null;
            emitValue();
        }, 0);
    }, [disabled, emitValue]);
    const handleKeyDown = useCallback((event) => {
        if (disabled || event.key !== "Enter")
            return;
        const nativeEvent = event.nativeEvent;
        const isComposing = composingRef.current
            || nativeEvent.isComposing
            || nativeEvent.keyCode === 229
            || compositionJustEndedRef.current;
        if (isComposing) {
            compositionJustEndedRef.current = false;
            return;
        }
        if (event.shiftKey) {
            event.preventDefault();
            insertPlainText("\n");
            return;
        }
        event.preventDefault();
        if (readValue().trim()) {
            void onSend();
        }
    }, [disabled, insertPlainText, onSend, readValue]);
    const composerClassName = className
        ? `plain-text-composer ${className}`
        : "plain-text-composer";
    return (_jsx("div", { ref: composerRef, className: composerClassName, style: { whiteSpace: "pre-wrap", overflowY: "auto", ...style }, role: "textbox", "aria-multiline": "true", "aria-label": ariaLabel || placeholder || undefined, "aria-placeholder": placeholder || undefined, "aria-disabled": disabled || undefined, "data-placeholder": placeholder || undefined, "data-testid": dataTestId, contentEditable: !disabled, suppressContentEditableWarning: true, spellCheck: true, tabIndex: disabled ? -1 : 0, onInput: handleInput, onPaste: handlePaste, onDrop: handleDrop, onDragOver: handleDragOver, onKeyDown: handleKeyDown, onCompositionStart: handleCompositionStart, onCompositionEnd: handleCompositionEnd }));
};
export default PlainTextComposer;
